import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Button, Typography, Container, Grid, Snackbar, Alert } from '@mui/material';
import { Analytics as AnalyticsIcon } from '@mui/icons-material';
import axios from 'axios';

// Components
import ImageUploadSection from '../components/analysis/ImageUploadSection';
import TradeDetailsForm from '../components/analysis/TradeDetailsForm';
import TradeHistorySection from '../components/analysis/TradeHistorySection';
import TradeModal from '../components/TradeModal';

// Hooks
import { useImageAnalysis } from '../hooks/useImageAnalysis';
import useTradeFilters from '../hooks/useTradeFilters';
import { useSettings } from '../contexts/SettingsContext';
import { calculateTradeMetrics } from '../utils/tradeUtils';

const ImageAnalysisPage = () => {
  const location = useLocation();
  const { settings } = useSettings();
  const [strategies, setStrategies] = useState([]);
  const [trades, setTrades] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [modalOpen, setModalOpen] = useState(false);

  const {
    imageFile, previewUrl, tradeData, setTradeData, loadingModel, metrics,
    handleFileSelect, handleDrop, analyzeImage, createTrade
  } = useImageAnalysis();

  const {
    selectedCategory, selectedSymbol, selectedStrategy, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol, setSelectedStrategy, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  const handleEditTrade = useCallback((trade) => {
    const now = new Date();
    const defaultDate = now.toISOString().split('T')[0];
    const defaultTime = now.toTimeString().split(' ')[0].slice(0, 5);
    
    setTradeData({
      ...trade,
      category: trade.category || 'stock',
      leverage: Number(trade.leverage) || 1,
      positionSize: Number(trade.positionSize) || '',
      strategyIds: trade.strategyIds || [],
      tags: trade.tags || '',
      tradeDate: trade.tradeDate || defaultDate,
      tradeTime: trade.tradeTime || defaultTime,
      closeDate: trade.closeDate || '',
      closeTime: trade.closeTime || '',
      rating: trade.rating || 3,
      exchange: trade.exchange || ''
    });
  }, []);

  useEffect(() => {
    fetchStrategies();
    fetchTrades();
    
    if (location.state?.editTrade) {
      handleEditTrade(location.state.editTrade);
    }
    
    // Listen for trade creation events
    const handleTradeCreated = () => {
      fetchTrades();
    };
    
    window.addEventListener('tradeCreated', handleTradeCreated);
    
    return () => {
      window.removeEventListener('tradeCreated', handleTradeCreated);
    };
  }, [location.state, handleEditTrade]);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(res.data);
      const strategyMap = {};
      for (const strat of res.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategyNamesMap(strategyMap);
    } catch (err) {
      console.error('Error fetching strategies:', err);
    }
  };

  const fetchTrades = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const res = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const sortedTrades = res.data.sort((a, b) => new Date(b.tradeDate) - new Date(a.tradeDate));
      setTrades(sortedTrades);
    } catch (err) {
      console.error('Error fetching trades:', err);
    }
  };

  const handleAnalyze = useCallback(async () => {
    const result = await analyzeImage();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
  }, [analyzeImage]);

  const handleSaveTrade = useCallback(async () => {
    const result = await createTrade();
    setSnackbar({ open: true, message: result.message, severity: result.success ? 'success' : 'error' });
    if (result.success) fetchTrades();
  }, [createTrade, fetchTrades]);

  const handleUpdateTrade = useCallback(async (updatedTrade) => {
    const original = trades.find(t => t.tradeId === updatedTrade.tradeId);
    const metrics = calculateTradeMetrics(
      updatedTrade.entryPrice,
      updatedTrade.stopLoss,
      updatedTrade.exitPrice,
      updatedTrade.positionSize,
      updatedTrade.leverage
    );
    
    if (original?.screenshot) updatedTrade.screenshot = original.screenshot;
    if (original?.imageUrl) updatedTrade.imageUrl = original.imageUrl;
    
    const payload = {
      ...updatedTrade,
      ...metrics,
      rrSummary: metrics.riskRewardRatio ? `R:R = ${metrics.riskRewardRatio} | Risk: $${metrics.riskAmount} | Reward: $${metrics.rewardAmount} | Quantity: ${metrics.quantity}` : ''
    };
    
    try {
      const token = localStorage.getItem('idToken');
      await axios.post('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/trades', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbar({ open: true, message: 'Trade updated', severity: 'success' });
      fetchTrades();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update trade', severity: 'error' });
    }
  }, [trades, fetchTrades]);

  const handleTradeDataChange = useCallback((field, value) => {
    setTradeData(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 } }}>
      {/* Header removed as requested */}

      {/* AI trade analysis, screenshot upload, and trade details sections removed as requested */}

      <TradeHistorySection
        filteredTrades={filteredTrades}
        selectedCategory={selectedCategory}
        selectedSymbol={selectedSymbol}
        selectedStrategy={selectedStrategy}
        selectedTags={selectedTags}
        startDate={startDate}
        endDate={endDate}
        onCategoryChange={setSelectedCategory}
        onSymbolChange={setSelectedSymbol}
        onStrategyChange={setSelectedStrategy}
        onTagsChange={setSelectedTags}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onTimeFilter={handleTimeFilter}
        symbols={symbols}
        strategies={strategies}
        allTags={allTags}
        strategyNamesMap={strategyNamesMap}
        onEditTrade={handleEditTrade}
        onUpdateTrade={handleUpdateTrade}
        onRefresh={fetchTrades}
        onDeleteSuccess={(msg) => setSnackbar({ open: true, message: msg, severity: 'success' })}
      />

      <TradeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveTrade}
        strategies={strategies}
        trades={trades}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ImageAnalysisPage;